import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link, type BlockerFunction, useBlocker, useNavigate } from 'react-router-dom';
import { Blockquote, Button, List, ListItem, Loading, Module, ModuleBody, ModuleFooter } from '@wfp/ui';

import {
	FullWarningEvent,
	Impact,
	Likelihood,
	type PartialFullWarningEvent,
	Seriousness,
	WarningEventStatus,
	WarningEventType,
} from '../../restapi/types';
import { useEWUser } from '../../hooks/home';
import { deleteEvent, isFirstEvent, reopenEvent, saveEvent, userCanEditEvent } from '../../restapi/warningEvent';
import { lastHomepageUrl } from '../../utils/navigation';
import { EventForm } from './EventForm';
import { AxiosError } from 'axios';
import { extractErrorMessages } from '../../restapi/restapi';
import { CommentsBox } from '../common/comments/CommentsBox';
import confirm from '../common/dialogs/Confirm';


interface Props {
	event: FullWarningEvent;
}

const noChanges: PartialFullWarningEvent = {};
const pageLeaveConfirmationMsg = 'There are unsaved changes. Do you really want to leave this page and lose the changes?';

function validateEvent(e: FullWarningEvent): string[] {
	const errors: string[] = [];
	if (e.likelihood === Likelihood.unspecified) {
		errors.push('Please specify the likelihood.');
	}
	if (e.impact === Impact.unspecified) {
		errors.push('Please specify the impact.');
	}
	if (e.seriousness < Seriousness.low) {
		errors.push('Please specify likelihood and impact that imply at least a low seriousness.');
	}

	if (isFirstEvent(e)) {
		if (!e.warning.title) {
			errors.push('Please specify a title.');
		}
		if (!e.warning.countries.length) {
			errors.push('Please specify at least one country.');
		}
		if (!e.warning.description) {
			errors.push('Please specify a description.');
		}
	}

	if ((e.type !== WarningEventType.closed) && !e.message) {
		errors.push('Please provide updates.');
	}


	return errors;
}

function mergeChanges(c1: PartialFullWarningEvent, c2: PartialFullWarningEvent) {
	// manually merge the warning partial sub-object properly
	let w = c1.warning;
	if (c2.warning) {
		w = {...(w || {}), ...c2.warning};
	}
	return {...c1, ...c2, warning: w};
}

export function EventEdit({event}: Props) {
	const user = useEWUser();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [isSaving, setIsSaving] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		window.onbeforeunload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = pageLeaveConfirmationMsg;
				return pageLeaveConfirmationMsg;
			}
		};
		return () => {
			window.onbeforeunload = null;
		};
	}, [isDirty]);

	const shouldBlock = React.useCallback<BlockerFunction>(
		({currentLocation, nextLocation}) =>
			(currentLocation.pathname !== nextLocation.pathname) && (isDirty),
		[isDirty],
	);
	const blocker = useBlocker(shouldBlock);

	useEffect(() => {
		if (blocker.state === 'blocked') {
			if (!isDirty) {
				blocker.proceed();
			} else {
				// just blocked with unsaved changes, ask confirmation
				confirm({
					title: 'Unsaved changes',
					message: pageLeaveConfirmationMsg,
					okLabel: 'Continue',
					cancelLabel: 'Cancel',
					type: 'danger',
				}).then(yes => {
					if (yes) {
						blocker.proceed();
					} else {
						blocker.reset();
					}
				});
			}
		}
	}, [blocker, isDirty]);

	const [errors, setErrors] = useState<string[]>([]);
	const [changes, setChanges] = useState<PartialFullWarningEvent>(noChanges);
	const saveMutation = useMutation((changes: PartialFullWarningEvent) => saveEvent(event, changes), {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			setChanges(noChanges);
			queryClient.invalidateQueries('event');
			queryClient.invalidateQueries('calendar');
			setTimeout(() => navigate(lastHomepageUrl));
		},
		onError: (error: AxiosError) => {
			const errors = extractErrorMessages(error);
			if (errors.length) {
				setErrors(errors);
			}
		},
		onSettled: (data) => {
			setIsSaving(false);
		},
	});

	useEffect(() => {
		setIsDirty(changes !== noChanges);
	}, [changes]);

	const validateAndSave = useCallback(
		() => {
			const errors = validateEvent(mergeChanges(event, changes) as FullWarningEvent);
			if (errors.length) {
				setErrors(errors);
			} else {
				setErrors([]);
				saveMutation.mutate(changes);
			}
		}, [event, changes, saveMutation],
	);

	const confirmDelete = useCallback(
		() => {
			const objName = isFirstEvent(event) ?
				'warning and all related updates'
				: 'update'
			confirm({
				title: 'Delete analysis',
				message: `This will delete the ${objName} permanently. Please click Delete to confirm.`,
				okLabel: 'Delete',
				cancelLabel: 'Cancel',
				type: 'danger',
			}).then(yes => {
				if (yes) {
					setIsSaving(true);
					deleteEvent(event).then(() => {
						setIsSaving(false);
						queryClient.invalidateQueries('event');
						queryClient.invalidateQueries('calendar');
						navigate(lastHomepageUrl);
					});
				}
			});
		}, [event, navigate, queryClient],
	);

	const reopenMutation = useMutation(() => reopenEvent(event), {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			queryClient.invalidateQueries('event');
			queryClient.invalidateQueries('calendar');
		},
		onSettled: () => {
			setIsSaving(false);
		},
	});

	const reopen = useCallback(
		() => {
			reopenMutation.mutate();
		}, [reopenMutation],
	);

	if (!user) {
		return null;
	}

	const approved = event.status === WarningEventStatus.approved;
	const canEdit = !approved && userCanEditEvent(user);
	const canDelete = canEdit && !!event.id;
	const canComment = !!event.id;
	const disabled = isSaving;
	const hasChanges = (changes !== noChanges);

	return (
		<Module>
			<ModuleBody>
				{approved && <Blockquote>
					<p>This {isFirstEvent(event) ? 'warning' : 'update'} has been approved and therefore is not
						editable.</p>
					{userCanEditEvent(user) && <>
						<p>If you want to amend the information contained here you can reopen it now: you will have to
							approve it again the the preview page.</p>
						<p className="mt right">
							<Button kind="primary" disabled={isSaving} onClick={() => reopen()}>Reopen now</Button>
						</p>
					</>}

				</Blockquote>}

				{canComment ? <CommentsBox event={event}/> : null}
				{canEdit ?
					<EventForm event={event} disabled={disabled}
							   onChange={c => setChanges(mergeChanges(changes, c))}
					/> :
					<EventForm event={event} disabled={true} onChange={() => {
					}}/>
				}
				{errors.length ?
					<div className="mt2">
						<Blockquote kind="error">
							<List kind="unordered">{errors.map((e, i) =>
								<ListItem key={i}>{e}</ListItem>,
							)}</List>
						</Blockquote>
					</div> : null}
			</ModuleBody>
			<ModuleFooter className="justify-content-right">
				{canDelete ? <Button disabled={disabled} kind="danger"
									 onClick={() => confirmDelete()}
				>Delete</Button> : <div/>}

				<div className="col-fill"/>
				{isSaving && <div><Loading withOverlay={false} active small/></div>}
				<div className="col-fill"/>

				<Link to={lastHomepageUrl}>
					<Button disabled={disabled} kind="secondary">Cancel</Button>
				</Link>

				{canEdit && <Button kind="primary" disabled={disabled || !hasChanges} className="ml"
									onClick={() => validateAndSave()}>Save</Button>}
			</ModuleFooter>
		</Module>
	);
}
